import {handleTracking} from '@jobseeker/common-core/utils';
import {
  OutlineCicleCheck,
  OutlineCircleCross,
} from '@jobseeker/lotus/assets/icons';
import {useIsLargeDevice, useWrappedTheme} from '@jobseeker/lotus/hooks';
import {PAGELIST_ID, getRefCodeAndTracking} from '@jobseeker/common-core/constants';
import {Box, Grid, List, ListItem, Typography, useMediaQuery} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {useEffect} from 'react';
import type {} from '@jobseeker/lotus/mui.types';
import {ProMembershipPromptType} from './types';
import PayuForm from './PayuForm';
import {useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import { PaidProducts } from '@jobseeker/feature-payment/constant';




const MembershipFeatures = ({planPopUp, source, queryParams}: ProMembershipPromptType) => {
  const theme = useWrappedTheme();
  const profile = useSelector((state: AppState) => state.user.profile);
  const showRecruiterActions = profile?.proMembershipFeatures?.statusChange || false;

  const isLarge = useIsLargeDevice();
  const isAboveMd = useIsLargeDevice('md');
  const isTablet = useMediaQuery('(min-width:768px)');
  const isMembershipPage = source === 'membership-page';

  // Override basic[1] (Application Status Notifications) when showRecruiterActions is true
  const basicValues = planPopUp?.basic
    ? planPopUp.basic.map((val, idx) => (idx === 1 && showRecruiterActions ? true : val))
    : [];
  useEffect(() => {
    const timer = setTimeout(() => {
      handleTracking(getRefCodeAndTracking(queryParams?.refcode), {
        event_category: 'UpgradeToPro',
        UserId: sessionStorage.getItem('userId') || 'NA',
        Device: sessionStorage.getItem('device') || 'desktop',
        Device_Type: sessionStorage.getItem('deviceType') || 'web',
        Status: sessionStorage.getItem('status') || 'LoggedOut',
      });
    }, 300); // Delay of 300ms
  
    // Cleanup the timer on component unmount
    return () => clearTimeout(timer);
  }, [queryParams?.refcode]);
  

  const ProButton = ({queryParams}: ProMembershipPromptType) => (
    <>
      <PayuForm
        key={'pro_membership_plan_button'}
        title={
          isMembershipPage ? 'Upgrade to PRO' : 'Buy Now @ &#8377;2000/6 months'
        }
        refCode={queryParams?.refcode || '0'}
        pageId={PAGELIST_ID[queryParams?.pageid as keyof typeof PAGELIST_ID] || '0'}
        tracking={{action: getRefCodeAndTracking(queryParams?.refcode), category: 'UpgradeToPro'}}
        sx={{width:228,height:32}}
        product={PaidProducts.PRO_MEMBERSHIP}
      />
      {isMembershipPage ? (
        <Typography
          fontSize={theme.typography.pxToRem(10)}
          color="rgba(0, 0, 0, 0.35)"
          fontStyle={'italic'}
          fontWeight={400}
          lineHeight={'14px'}
          letterSpacing={'0.32px'}
          mt={0.5}
          mb={{xxs: 5, md: 1.75}}
          display="block"
          sx={{maxWidth: 250, padding: 1}}
        >
          {`On click, you will be redirected to a third-party payment gateway, subject to its T&Cs.`}
        </Typography>
      ) : (
        <Typography
          fontSize={theme.typography.pxToRem(8)}
          fontStyle={'italic'}
          fontWeight={500}
          lineHeight={'10px'}
          letterSpacing={'0.32px'}
          mt={0.75}
          mb={{xxs: 0, md: 1.75}}
          display={{xxs: 'none', md: 'block'}}
        >
          `(Less than Rs 15/day)`
        </Typography>
      )}
    </>
  );
  const TickCheck = ({
    item,
    index,
    marginTop,
  }: {
    item: boolean;
    index: string;
    marginTop: {xxs?: number, xs?: number, sm?: number; md?: number};
  }) => (
    <Box mt={marginTop}>
      <Image
        key={index}
        src={item ? OutlineCicleCheck : OutlineCircleCross}
        alt={item ? 'Completed' : 'Uncompleted'}
        width={isLarge ? 28 : 16}
        height={isLarge ? 28 : 16}
        objectFit="cover"
      />
    </Box>
  );

  const INDEX_SPACING: {[key: string]: {[key: number]: number}} = {
    basic: {
      0: 2.2,
      1: 2.2,
      2: 5.5,
      3: 4.8,
      4: 2.25,
      5: 3,
    },
    pro: {
      0: 2.2,
      1: 2.2,
      2: 5.5,
      3: 4.8,
      4: 2.25,
      5: 3,
    },
  };

  const INDEX_SPACING_XS: {[key: string]: {[key: number]: number}} = {
    basic: {
      0: 3,
      1: 2.5,
      2: 4,
      3: 2,
      4: 2,
      5: 2.75,
    },
    pro: {
      0: 1.5,
      1: 2.5,
      2: 4,
      3: 2,
      4: 2,
      5: 2.5,
    },
  };

  return (
    <Grid
      container
      borderRadius={{xxs: 0, md: 4}}
      p={1}
      sx={{
        backgroundColor: 'border.main',
        mt: -1,
        justifyContent: 'center',
        height:{xxs:550,xs:500,md:600, sm: 'auto'},
        // width:{xxs:320},
      }}
    >
      <Grid
        item
        xxs={6.2}
        xs={isMembershipPage ? 5.2 : 6.2}
        sm={6.2}
        md={5.7}
        sx={{
          mt: {xxs: 3.6, md: 5.25},
          width: {xxs: 154, md: 344},
          '@media (max-width: 320px)': {
            width: 90,
          },
         
        }}
      >
        <Typography
          fontSize={{
            xxs: theme.typography.pxToRem(12),
            xs: theme.typography.pxToRem(12),
            sm: theme.typography.pxToRem(16),
            md: isMembershipPage
              ? theme.typography.pxToRem(20)
              : theme.typography.pxToRem(14),
          }}
          fontWeight={600}
          lineHeight={{xxs: '16px', md: '24px'}}
          mb={1.5}
          sx={{ '@media (max-width: 320px)': {
            fontSize:theme.typography.pxToRem(8)
          },}}
        >
          Membership Features
        </Typography>
        <List
          sx={{
            paddingLeft: theme.spacing(2),
          }}
        >
          {planPopUp?.infos.map((item, index) => {
            return (
              <ListItem
                component="a"
                key={`profiledrawer-listitem-${index}`}
                title={item.title}
                target="_self"
                sx={{
                  display: 'list-item',
                  listStyleType: 'disc',
                  fontSize: {
                    xxs: theme.typography.pxToRem(12),
                    xs:theme.typography.pxToRem(12),
                    sm: theme.typography.pxToRem(14),
                    md: isMembershipPage
                      ? theme.typography.pxToRem(16)
                      : theme.typography.pxToRem(12),
                  },
                  fontWeight: {
                    xxs: theme.typography.fontWeightMedium,
                    md: theme.typography.fontWeightBold,
                  },
                  width:{xxs:95,xs: 115, sm: 335, md:400},
                  whiteSpace: 'wrap',
                  color: theme.palette.secondary.main,
                  // lineHeight:{xs:'10px'},
                  wordBreak: 'break-word',
                  ':hover': {},
                  p: `0px 0px ${isLarge ? '14px' : '24px'} 0px`,
                }}
              >
                {item.title}
                <br />
                {isLarge && (
                  <Typography
                    sx={
                      isMembershipPage
                        ? {
                            fontSize: theme.typography.pxToRem(14),
                            fontWeight: 400,
                            lineHeight: '18px',
                          }
                        : null
                    }
                    variant={!isMembershipPage ? 'body3' : undefined}
                    color={theme.palette.text.secondary}
                  >
                    {item.subTitle}
                  </Typography>
                )}
              </ListItem>
            );
          })}
        </List>
      </Grid>
      <Grid
        item
        display={'flex'}
        ml={{xxs: 2, xs:1, md: 4}}
        mt={{xxs: 2, sm: 1}}
        textAlign={'center'}
      >
        <Box
          borderRadius={4}
          display={'inline-block'}
          boxShadow={`0px 6.48px 6px 2px rgba(41, 41, 41, 0.02) inset`}
          sx={{
            backgroundColor: 'white',
            width: {xxs: '48px', md: '164px',sm:'80px'},
            height: {xxs: '323px', sm: '456px', md: '456px'},
            '@media (max-width: 320px)': {
              width: 50,
              height:400
            },
          }}
        >
          <Typography
            color={'text.secondary'}
            fontSize={{xxs: 10, md: 16}}
            sx={{
              px: {xxs: 1.25, md: 4.75},
              pt: {xxs: 1.625, md: 5.25},
            }}
            fontWeight={theme.typography.fontWeightMedium}
          >
            Basic
          </Typography>
          {basicValues.map((item, index) => (
            <TickCheck
              key={`basic_${index}`}
              item={item}
              index={`basic_${index}`}
              marginTop={{xxs: 3, xs: INDEX_SPACING_XS['basic'][index], sm: 3.5, md: INDEX_SPACING['basic'][index]}}
            />
          ))}
        </Box>
        <Box
          borderRadius={{xxs: 1.5, md: 4}}
          border={`1px solid ${theme.palette.border.light}`}
          display={'inline-block'}
          boxShadow={`0px 6.48px 6px 2px rgba(41, 41, 41, 0.02)`}
          sx={{
            backgroundColor: 'white',
            width: {xxs: '94px', xs: '110px', sm: '120px', md: '252px'},
            height:{xs:'323px',md:'546px', sm: 'auto'},
            flexShrink: 0,
          }}
        >
          <Typography
            color={'white'}
            fontSize={{
              xxs: theme.typography.pxToRem(10),
              md: theme.typography.pxToRem(16),
            }}
            fontWeight={600}
            // lineHeight={'24px'}
            sx={{
              borderRadius: {xxs: 1.5, md: 4},
              px: isMembershipPage ? 'none' : {xxs: 4.25, md: 11.5},
              py: {xxs: 1, md: isMembershipPage ? 1 : 2.25},
              margin: 0.25,
              background: 'linear-gradient(90deg, #225448 0%, #7E9F67 100%)',
              lineHeight: {xs: '11px', sm: '12px', md: '24px'},
            }}
          >
            {source === 'membership-page' ? (
              <>
                PRO <br />
                (Rs 2000/6 months)
              </>
            ) : (
              'PRO'
            )}
          </Typography>
          {planPopUp?.pro.map((item, index) => (
            <TickCheck
              key={`pro_${index}`}
              item={item}
              index={`pro_${index}`}
              marginTop={{
                xxs: index > 0 ? 2.9 : 1.5,
                xs: INDEX_SPACING_XS['pro'][index],
                sm: index > 0 ? 3.5 : 1.75, 
                md: INDEX_SPACING['pro'][index],
              }}
            />
          ))}
          {isAboveMd && <ProButton queryParams={queryParams} />}
        </Box>
      </Grid>
      <Grid item>
        <Box display={{xxs: 'block', md: 'none'}} textAlign={'center'} marginTop={isTablet?6:0}>
          <ProButton queryParams={queryParams}/>
        </Box>
      </Grid>
    </Grid>
  );
};

export default MembershipFeatures;
